import { SnackbarProvider } from 'notistack';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { applyMiddleware, combineReducers, createStore, compose } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger'; // Add redux-logger for better debugging (optional)
import App from './App';
import './index.css';
import * as myServiceWorker from './myServiceWorker';
import authReducer from './store/Auth/Auth.reducer';
import configReducer from './store/config/config.reducer';
import langReducer from './store/lang/lang.reducer';
import loadingReducer from './store/loading/reducer';
import newAddressReducer from './store/NewAddress/NewAddress.reducer';
import newPatientReducer from './store/NewPatient/NewPatient.reducer';
import { diffRetainerReducer } from './store/DiffRetainers/reducer';
import requestFinishingRetainerReducer from './store/FinishingRetainer/FinishingRetainer.reducer';
import notiReducer from './store/Noti/noti.reducers';
import resReducer from './store/res/res.reducer';
import updatingAccountReducer from './store/UpdatingAccount/UpdatingAccount.reducer';
import systemAnnouncementReducer from './store/SystemAnnouncement/systemAnnouncement.reducer';
import requestCommentReducer from './pages/ClinicalSimulation/RequestComment/RequestCommentSlice';
import { traceLogReducer } from './store/TraceLog/tracelog.reducer';
import retrackReducer from './store/Retrack/RetrackSlice';
import errorMessageReducer from './store/ErrorMessage/ErrorMessageSlice';
import smileDesignReducer from './store/SmileDesign/SmileDesignSlice';
import treatmentPlanApprovalReducer from './store/TreatmentPlanApproval/TreatmentPlanApprovalSlice';
import uploadFileReducer from './store/UploadFile/UploadFileSlice';
import newCaseReducer from './store/NewCase/NewCase.reducer';
import { SubPath } from './config.json';
import * as connectPlatform from './store/ConnectPlatform/connectPlatform';
import { globalReducer } from './store/Global/global';
import { clinicalSimulationReducer } from './store/ClinicalSimulation/clinicalSimulation';
import { enableMapSet } from 'immer';
import { SentryDNSUrl } from './config.json';
import * as Sentry from '@sentry/react';
import { userAllowedFeaturesReducer } from './store/UserAllowedFeatures/UserAllowedFeaturesSlice';

console.log('SENTRY ENABLE:', process.env.REACT_APP_ENABLE_SENTRY);
if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
    Sentry.init({
        dsn: SentryDNSUrl,
        debug: true,
        sampleRate: 1,
        release: '0.1.0', // Refer to packcage.json's version
        integrations: [
            Sentry.browserTracingIntegration(),
            // Sentry.replayIntegration({
            //   maskAllText: false,
            //   blockAllMedia: false,
            // }),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost:3000'],
        // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
    console.log('Sentry initialized');
}

// Enable support for Map and Set for Immer(redux-toolkit)
enableMapSet();

const reducers = combineReducers({
    requestFinishingRetainerReducer,
    newPatientReducer,
    newAddressReducer,
    auth: authReducer,
    lang: langReducer,
    config: configReducer,
    loading: loadingReducer,
    updatingAccount: updatingAccountReducer,
    res: resReducer,
    noti: notiReducer,
    systemAnnouncement: systemAnnouncementReducer,
    traceLogReducer,
    requestComment: requestCommentReducer,
    retrack: retrackReducer,
    errorMessage: errorMessageReducer,
    smileDesign: smileDesignReducer,
    treatmentPlanApproval: treatmentPlanApprovalReducer,
    connectPlatformReducer: connectPlatform.connectPlatformReducer,
    newCaseReducer,
    uploadFile: uploadFileReducer,
    diffRetainer: diffRetainerReducer,
    globalReducer: globalReducer,
    clinicalSimulation: clinicalSimulationReducer,
    userAllowedFeatures: userAllowedFeaturesReducer,
});

const rootReducer = (state, action) => {
    if (action.type === 'NEW_PATIENT_DATA_SAVE_SUCCESS') {
        state = undefined;
    }

    return reducers(state, action);
};

// Add remote-redux-devtools configuration
const composeEnhancers =
    process.env.NODE_ENV === 'development'
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || composeWithDevTools({ trace: true, traceLimit: 25 })
        : compose;

const middleware = [thunk];
if (process.env.NODE_ENV === 'development') {
    middleware.push(createLogger()); // Add redux-logger for better debugging (optional)
}

const enhancer = compose(composeEnhancers(applyMiddleware(...middleware)));

const store = createStore(rootReducer, enhancer);

ReactDOM.render(
    <SnackbarProvider
        maxSnack={5}
        disableWindowBlurListener
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
        }}
    >
        <Provider store={store}>
            <Router basename={SubPath}>
                <App />
            </Router>
        </Provider>
    </SnackbarProvider>,
    document.getElementById('root')
);

myServiceWorker.init();
